/** biome-ignore-all lint/performance/noNamespaceImport: expedted */
import { CheckIcon, ChevronsUpDownIcon } from "lucide-react";
import * as React from "react";
import * as RPNInput from "react-phone-number-input";
import flags from "react-phone-number-input/flags";
import { Button } from "@/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn, type SafeOmit } from "@/lib/utils";

type PhoneInputProps = SafeOmit<React.InputHTMLAttributes<HTMLInputElement>, "onChange" | "value"> &
	SafeOmit<RPNInput.Props<typeof RPNInput.default>, "onChange"> & {
		onChange?: (value: RPNInput.Value) => void;
	};

const PhoneInput: React.ForwardRefExoticComponent<PhoneInputProps> = React.forwardRef<
	React.ComponentRef<typeof RPNInput.default>,
	PhoneInputProps
>(({ className, onChange, value, ...props }, ref) => (
	<RPNInput.default
		className={cn("flex", className)}
		countrySelectComponent={(p: CountrySelectProps) => <CountrySelect {...p} />}
		flagComponent={FlagComponent}
		inputComponent={InputComponent}
		onChange={(v) => onChange?.(v || "")}
		ref={ref}
		/**
		 * Handles the onChange event.
		 *
		 * react-phone-number-input might trigger the onChange event as undefined
		 * when a valid phone number is not entered. To prevent this,
		 * the value is coerced to an empty string.
		 *
		 * @param {E164Number | undefined} value - The entered value
		 */
		value={value === "" || value === undefined ? "" : value}
		{...props}
	/>
));
PhoneInput.displayName = "PhoneInput";

const InputComponent = React.forwardRef<HTMLInputElement, React.ComponentProps<"input">>(
	({ className, ...props }, ref) => (
		<Input className={cn("rounded-s-none rounded-e-lg", className)} {...props} ref={ref} />
	)
);
InputComponent.displayName = "InputComponent";

type CountrySelectOption = { label: string; value: RPNInput.Country };

type CountrySelectProps = {
	disabled?: boolean;
	value: RPNInput.Country;
	onChange: (value: RPNInput.Country) => void;
	options: Array<CountrySelectOption>;
};

const CountrySelect = ({ disabled, value, onChange, options }: CountrySelectProps) => {
	const handleSelect = React.useCallback((country: RPNInput.Country) => onChange(country), [onChange]);

	return (
		<Popover>
			<PopoverTrigger asChild>
				<Button
					className={cn("flex gap-1 rounded-s-lg rounded-e-none px-3")}
					disabled={disabled}
					type="button"
					variant="outline"
				>
					<FlagComponent country={value} countryName={value} />
					<ChevronsUpDownIcon className={cn("-mr-2 size-4 opacity-50", disabled ? "hidden" : "opacity-100")} />
				</Button>
			</PopoverTrigger>
			<PopoverContent className="w-75 p-0">
				<Command>
					<CommandInput placeholder="Search country..." />
					<CommandEmpty>No country found.</CommandEmpty>
					<CommandList>
						<CommandGroup>
							{options.map((option) => (
								<CommandItem className="gap-2" key={option.value || "ZZ"} onSelect={() => handleSelect(option.value)}>
									<FlagComponent country={option.value} countryName={option.label} />
									<span className="flex-1 text-sm">{option.label}</span>
									{option.value && (
										<span className="text-foreground/50 text-sm">
											{`+${RPNInput.getCountryCallingCode(option.value)}`}
										</span>
									)}
									<CheckIcon className={cn("ml-auto size-4", option.value === value ? "opacity-100" : "opacity-0")} />
								</CommandItem>
							))}
						</CommandGroup>
					</CommandList>
				</Command>
			</PopoverContent>
		</Popover>
	);
};

export const FlagComponent = ({ country, countryName }: RPNInput.FlagProps) => {
	const Flag = flags[country];

	return (
		<span className="flex h-4 w-6 overflow-hidden rounded-sm bg-foreground/20 [&_svg]:size-full!">
			{Flag && <Flag title={countryName} />}
		</span>
	);
};
FlagComponent.displayName = "FlagComponent";

export { PhoneInput };
